export const contract = {
  list: {
    title: 'Contracts',
    searchPlaceholder: 'Search by contract, number, partner or vehicle...',
    loadError: 'Failed to load contracts.',
    result: {
      singular: 'contract',
      plural: 'contracts',
    },
    tabs: {
      purchase: 'Purchase',
      leasing: 'Leasing',
      insurance: 'Insurance',
    },
    columns: {
      contractKey: 'Contract Key',
      name: 'Contract Name',
      vehicles: 'Vehicles',
      contractPartner: 'Contract Partner',
      startDate: 'Start Date',
      endDate: 'End Date',
      status: 'Status',
      paymentMethod: 'Payment Method',
      externalContractNumber: 'External Contract Number',
      condition: 'New / used vehicle',
      purchaseDate: 'Purchase date',
      purchasePrice: 'Purchase price',
      warranty: 'Warranty',
    },
    columnGroups: {
      base: 'Base fields',
      conditions: 'Conditions',
      commercial: 'Commercial',
    },
    status: {
      active: 'Active',
      ended: 'Ended',
    },
    vehicles: {
      filterAssigned: 'Vehicle assigned',
      filterNotAssigned: 'No vehicle assigned',
      hidden: 'Vehicle hidden',
    },
    nextEnd: {
      label: 'Next end',
      within90: 'Ends within 90 days',
      within180: 'Ends within 180 days',
      noEnd: 'No end known',
    },
    empty: {
      purchase: 'No purchase contracts yet.',
      leasing: 'No leasing contracts yet.',
      insurance: 'No insurance contracts yet.',
      purchaseDescription: 'Document purchase contracts for vehicles that already exist here.',
    },
    noResults: {
      title: 'No contracts match your search',
      description: 'Try a different search term, or reset your search and filters.',
      reset: 'Reset search and filters',
    },
    addContract: 'Add contract',
  },
  create: {
    title: 'Add contract',
    noPermission: 'You do not have permission to create contracts in this tenant.',
    wizard: {
      step: {
        'type-and-vehicle': { title: 'Contract type & vehicle' },
        'master-data': { title: 'Master data' },
        conditions: { title: 'Conditions' },
        commercial: { title: 'Commercial' },
      },
      conditionsPlaceholder: 'Conditions for this contract type aren’t available yet.',
      noVehicleYet: 'No vehicle yet',
      nav: {
        stepCount: 'Step {{current}} of {{total}}',
        fieldCount: '{{filled}}/{{total}} filled',
        back: 'Back',
        next: 'Next',
        cancel: 'Cancel',
        submit: 'Add a contract',
        submitting: 'Adding…',
      },
      typeSwitchConfirm: {
        title: 'Change the contract type?',
        description:
          'The entries of the previous domain fields are discarded. Master data and commercial details are kept.',
        confirm: 'Switch and discard',
        cancel: 'Cancel',
      },
      validation: {
        blockedNext: 'Please fill in {{field}} before continuing.',
      },
    },
    fields: {
      name: 'Contract name',
      type: 'Contract type',
      contractPartner: 'Contract partner',
      contactPerson: 'Contact person',
      startDate: 'Start date',
      purchaseDate: 'Purchase date',
      currency: 'Currency',
      externalContractNumber: 'External contract number',
      vehicleCondition: 'Vehicle condition',
      mileageAtPurchase: 'Mileage at purchase',
      ordinaryTermination: 'Ordinary termination possible',
      noticePeriod: 'Notice period',
      automaticRenewal: 'Automatic renewal',
      owner: 'Owner',
      registeredKeeper: 'Registered keeper',
      financingType: 'Financing type',
      orderDate: 'Order date',
      plannedDeliveryDate: 'Planned delivery date',
      actualDeliveryDate: 'Actual delivery date',
      paymentTermDays: 'Payment term in days',
      dueDate: 'Due date',
    },
    financingType: {
      cashPurchase: 'Cash purchase',
      loan: 'Loan',
      hirePurchase: 'Hire purchase',
      other: 'Other',
    },
    financingNotice:
      'Loan and hire purchase are not modelled in V1. The financing type is documented; repayment plan, remaining debt and interest follow with the financing record.',
    cards: {
      ownershipFinancing: 'Ownership and financing',
      orderDelivery: 'Order and delivery',
      priceTerms: 'Price and terms',
      warranty: 'Warranty',
      statutoryWarranty: 'Statutory warranty',
      taxAccounting: 'Tax and accounting',
    },
    warranty: {
      add: 'Add warranty',
      remove: 'Remove warranty: {{type}}',
      empty: 'No warranties recorded yet.',
      fields: {
        type: 'Warranty type',
        provider: 'Warranty provider',
        start: 'Start',
        term: 'Term',
        end: 'End',
        mileageLimit: 'Mileage limit',
      },
      type: {
        manufacturer: 'Manufacturer',
        extended: 'Extended',
        mobility: 'Mobility',
        battery: 'Battery',
        paint: 'Paint',
        antiCorrosion: 'Anti-corrosion',
        dealer: 'Dealer',
        other: 'Other',
      },
    },
    statutoryWarranty: {
      start: 'Statutory warranty start',
      term: 'Statutory warranty term',
      end: 'Statutory warranty end',
    },
    taxAccounting: {
      depreciationMethod: 'Depreciation method',
      capitalisationDate: 'Capitalisation date',
      depreciationStart: 'Depreciation start',
      plannedResidualValue: 'Planned residual value',
      usefulLifeYears: 'Useful life',
      remainingUsefulLifeYears: 'Remaining useful life',
      depreciationRate: 'Depreciation rate',
      depreciationAnnualAmount: 'Annual depreciation',
      depreciationMethodValues: {
        straightLine: 'Straight-line',
        decliningBalance: 'Declining balance',
        performanceBased: 'Performance-based',
        special: 'Special',
        impairment: 'Impairment',
        accelerated: 'Accelerated',
      },
      depreciationNotice:
        'Only straight-line depreciation is calculated in V1. The chosen method is documented; declining balance, performance-based, special, impairment and accelerated depreciation need period data and a versioned tax rule set.',
      usefulLifeYearsOption_one: '{{count}} year',
      usefulLifeYearsOption_other: '{{count}} years',
    },
    vehicleCondition: {
      new: 'New',
      used: 'Used',
    },
    yesNo: {
      yes: 'Yes',
      no: 'No',
    },
    units: {
      months: 'months',
      days: 'days',
      years: 'years',
    },
    priceChain: {
      newVehicle: {
        basePriceNet: 'Base vehicle price',
        specialEquipment: 'Special equipment',
        accessories: 'Accessories',
        conversionCost: 'Conversion cost',
        discountPercent: 'Discount in percent',
        subsidyBonus: 'Environmental or subsidy bonus',
        listPriceNet: 'List price net',
        listPriceGross: 'List price gross',
        discountAmount: 'Discount amount',
        purchasePriceNet: 'Purchase price net',
        tax: 'Tax',
        purchasePriceGross: 'Purchase price gross',
      },
      usedVehicle: {
        totalPurchasePrice: 'Total purchase price',
        newAccessories: 'New accessories',
        newConversionCost: 'New conversion cost',
        taxTreatment: 'Tax treatment',
        purchasePriceGross: 'Purchase price gross',
        totalPrice: 'Total price',
        purchasePriceNet: 'Purchase price net',
        inputTaxAmount: 'Input tax amount',
      },
      shared: {
        transferCost: 'Transfer cost',
        registrationCost: 'Registration cost',
        taxRate: 'Tax rate',
      },
      taxTreatment: {
        standardVat: 'Standard VAT',
        marginScheme: 'Margin scheme',
        privateSale: 'Private sale / no VAT',
      },
    },
    originalCondition: {
      title: 'As originally bought',
      originalBasePrice: 'Original base price',
      originalAccessories: 'Accessories when new',
      originalConversionCost: 'Earlier conversion cost',
    },
    placeholders: {
      name: {
        leasing: 'Leasing Audi A6 Avant',
        purchase: 'Kauf Audi A6 Avant',
        insurance: 'Kfz Versicherung Audi A6',
      },
      contractPartner: {
        leasing: 'ALD Automotive',
        purchase: 'Autohaus Sonnenberg GmbH',
        insurance: 'HUK-COBURG Versicherung',
      },
      externalContractNumber: {
        leasing: 'ALD-884213',
        purchase: 'AH-2025-0912',
        insurance: 'VS-2026-4711',
      },
    },
    validation: {
      required: 'This field is required.',
    },
    actions: {
      save: 'Add a contract',
      saving: 'Saving…',
      cancel: 'Cancel',
    },
    toast: {
      created: 'Contract created',
      saveFailed: 'Failed to create the contract.',
    },
  },
  detail: {
    actions: {
      edit: 'Edit',
      save: 'Save',
      saving: 'Saving…',
      cancel: 'Cancel',
    },
    tabs: {
      contractData: 'Contract data',
      commercial: 'Commercial',
    },
    toast: {
      saved: 'Contract saved',
      saveFailed: 'Failed to save the contract.',
      concurrentEdit: 'The contract has changed in the meantime. Your entries are kept — please check and save again.',
      clearNotSupported: 'This field cannot be cleared yet, so its previous value was kept.',
    },
    noAccountingAccess: 'Your role cannot see Commercial details.',
    currencyLocked: 'No longer changeable — the contract has been created.',
  },
  commercial: {
    fields: {
      billingBasis: 'Billing basis',
      firstDueDate: 'First due date',
      dueDay: 'Due day',
      paymentPeriod: 'Payment period',
      paymentTiming: 'Payment timing',
      paymentMethod: 'Payment method',
    },
    billingBasis: {
      contract: 'Contract',
      permanentInvoice: 'Permanent invoice',
      individualInvoice: 'Individual invoice',
    },
    paymentPeriod: {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semiAnnually: 'Semi-annually',
      annually: 'Annually',
      oneOff: 'One-off',
    },
    paymentTiming: {
      advance: 'In advance',
      arrears: 'In arrears',
      unknown: 'Unknown',
    },
    paymentMethod: {
      bankTransfer: 'Bank transfer',
      directDebit: 'Direct debit',
      financing: 'Financing',
      other: 'Other',
    },
  },
  vehicles: {
    title: 'Vehicles',
    assign: 'Assign vehicle',
    assignDisabledReason: 'A contract carries exactly one vehicle. Remove the current one first.',
    remove: 'Remove',
    ended: 'Ended',
    noVehicleAssigned: 'No vehicle assigned',
    vehicleHidden: 'Vehicle hidden',
    loading: 'Loading vehicles…',
    loadError: 'Failed to load vehicles.',
    columns: {
      licensePlate: 'Licence plate',
      vin: 'VIN',
      validFrom: 'Valid from',
      validUntil: 'Valid until',
    },
    assignDialog: {
      title: 'Assign vehicle',
      vehicleLabel: 'Vehicle',
      vehiclePlaceholder: 'Search by licence plate or VIN…',
      validFrom: 'Valid from',
      validUntil: 'Valid until',
      submit: 'Assign',
      submitting: 'Assigning…',
      cancel: 'Cancel',
    },
    removeConfirm: {
      title: 'Remove {{vehicle}} from this contract?',
      description: 'The vehicle stays visible in the contract history.',
      confirm: 'Remove',
      confirming: 'Removing…',
      cancel: 'Cancel',
    },
    toast: {
      assigned: '1 vehicle assigned',
      removed: 'Vehicle removed',
      assignFailed: 'Failed to assign the vehicle.',
      removeFailed: 'Failed to remove the vehicle.',
    },
  },
  status: {
    values: {
      active: 'Active',
      ended: 'Ended',
    },
    endingDialog: {
      title: 'End this contract?',
      type: 'Type of ending',
      typeValues: {
        regularExpiry: 'Expired as agreed',
        termination: 'Terminated',
      },
      effectiveDate: 'Ended on',
      noticeGivenDate: 'Notice given on',
      wirkungZum: 'Effective on',
      agreedEndDate: 'Agreed end',
      noNoticePeriod: 'No notice period on record — please enter the day the termination takes effect.',
      reason: 'Reason',
      reasonPlaceholder: 'Which reason?',
      reasonText: 'Reason (other)',
      confirm: 'End',
      cancel: 'Cancel',
      reasons: {
        termExpired: 'Term expired',
        totalMileageReached: 'Total mileage reached',
        notRenewed: 'Not renewed',
        terminatedByUs: 'Terminated by us',
        terminatedByPartner: 'Terminated by the contract partner',
        terminationAgreement: 'Termination agreement',
        vehicleSold: 'Vehicle sold',
        totalLoss: 'Total loss',
        vehicleReturnedEarly: 'Vehicle returned early',
        paymentDefault: 'Payment default',
        other: 'Other reason',
      },
    },
    endConfirm: {
      title: 'End this contract?',
      description_one:
        '{{count}} position has no valid-until date. It stays unchanged — the contract still counts as ended.',
      description_other:
        '{{count}} positions have no valid-until date. They stay unchanged — the contract still counts as ended.',
      confirm: 'End',
      cancel: 'Cancel',
    },
    reactivateConfirm: {
      title: 'Set the contract to active again?',
      description: 'The status changes to active. The recorded ending and its reason are removed.',
      confirm: 'Set active',
      confirming: 'Setting active…',
      cancel: 'Cancel',
    },
    endingCard: {
      title: 'Ending',
      type: 'Type of ending',
      noticeGivenDate: 'Notice given on',
      effectiveDate: 'Effective on',
      agreedEndDate: 'Agreed end',
      reason: 'Reason',
      recorded: 'Recorded',
      recordedBy: '{{name}} on {{date}}',
      noReasonRecorded: 'Not recorded',
    },
    toast: {
      ended: 'Contract ended',
      terminated: 'Contract terminated',
      active: 'Contract active again',
      endFailed: 'Failed to end the contract.',
      reactivateFailed: 'Failed to reactivate the contract.',
    },
  },
};

export type ContractResources = typeof contract;
